import { useState, useCallback, useRef, useEffect } from 'react';
import * as orderService from '@/supabase/order/service/orderService';
import { useAuth } from './useAuth';
import { logError } from '@/lib/sentry';
import {
  DashboardSuggestions,
  SearchedOrder,
  OrderSearchMode,
  OrderSearchMatchType,
} from '@/supabase/order/types/orderSearch';

const EMPTY_SUGGESTIONS: DashboardSuggestions = { customers: [], products: [] };

// 원자적 단일 검색 결과 state — orders/isPartial/mode를 한 객체로 묶어 미러 state 방지.
// query = 검색 시점에 고정된 검색어(결과 하이라이트 기준). 이후 입력창 타이핑에 흔들리지 않음(memo 안정).
interface UnifiedSearchResult {
  orders: SearchedOrder[];
  isPartial: boolean;
  mode: OrderSearchMode;
  query: string;
}

export function useUnifiedOrderSearch() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<DashboardSuggestions>(EMPTY_SUGGESTIONS);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<UnifiedSearchResult | null>(null);

  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // seq 가드 2개 — 연타 시 stale 응답이 최신 결과/제안을 덮지 않게 (useDashboardData summarySeqRef 패턴).
  const searchSeqRef = useRef(0);
  const suggestSeqRef = useRef(0);

  // unmount cleanup — PR2 codexloop에서 연기된 항목, 이번에 정식 포함.
  useEffect(() => () => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    // in-flight 응답의 setState를 seq 게이트가 걸러냄 — React 18에선 no-op이지만 방어 계층 명시 (codexloop PR3-R1 M3).
    searchSeqRef.current++;
    suggestSeqRef.current++;
  }, []);

  const closeSuggestions = useCallback(() => setShowSuggestions(false), []);
  const openSuggestions = useCallback(() => setShowSuggestions(true), []);

  const onQueryChange = useCallback((q: string) => {
    setSearchQuery(q);
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    // 입력 변경 즉시 in-flight suggestion 무효화 — 늦게 도착한 응답이 최신 입력 기준 제안을 덮지 않게 (codexloop PR3-R1 H2).
    suggestSeqRef.current++;
    if (!q.trim()) {
      setSuggestions(EMPTY_SUGGESTIONS);
      setShowSuggestions(false);
      setIsSuggesting(false); // in-flight 무효화 시 스피너 잔존 방지 (codexloop PR3-R1 H2).
      return;
    }
    debounceTimeoutRef.current = setTimeout(async () => {
      const seq = ++suggestSeqRef.current;
      setIsSuggesting(true);
      setShowSuggestions(true);
      try {
        const { customers, products } = await orderService.getDashboardSearchSuggestions(q);
        if (seq !== suggestSeqRef.current) return; // stale suggestion 응답 무시
        // customers exact-match 제외 필터는 훅 소유(카드에서 재필터 금지). trim 기준 정규화된 exact 매칭.
        const filteredCustomers = customers.filter((s) => s.toLowerCase() !== q.trim().toLowerCase());
        setSuggestions({ customers: filteredCustomers, products });
      } catch (err) {
        if (seq !== suggestSeqRef.current) return;
        logError('대시보드 검색 제안 조회 실패:', err);
        setSuggestions(EMPTY_SUGGESTIONS);
      } finally {
        if (seq === suggestSeqRef.current) setIsSuggesting(false);
      }
    }, 150);
  }, []);

  // 검색 3함수 공통 러너 — !user || !q.trim() 가드(result 초기화 후 return, 기존 fetchOrdersByNickname 미러).
  const runSearch = useCallback(async (
    mode: OrderSearchMode,
    q: string,
    fetcher: (userId: string) => Promise<{ orders: SearchedOrder[]; isPartial: boolean }>,
  ) => {
    // 검색 실행 시 pending 디바운스 제거 — 150ms 내 타이핑+Enter 시 옛 부분 query로
    // 드롭다운이 결과 위에 재오픈되는 글리치 방지 (태스크 리뷰 Minor #1, e2e 시나리오7 실증)
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    // 검색 실행이 in-flight suggestion을 무효화 — 검색 중 드롭다운 스피너/제안 잔존 방지 (codexloop PR3-R1 H2).
    suggestSeqRef.current++;
    setIsSuggesting(false);
    // 검색 후보 state도 비움 — 닫힌 드롭다운을 ArrowDown으로 재오픈할 때 이전 prefix 기준
    // stale 목록이 되살아나지 않게 (codexloop PR3-R2 #1).
    setSuggestions(EMPTY_SUGGESTIONS);
    if (!user || !q.trim()) {
      // 진행 중 검색 무효화 — 비운 결과를 늦게 도착한 stale 응답이 덮지 않게 (태스크 리뷰 Minor #2)
      searchSeqRef.current++;
      setResult(null);
      // in-flight 검색의 finally가 seq mismatch로 setIsLoading(false)를 건너뛰므로 가드가 직접 정리 (codexloop PR3-R1 H1).
      setIsLoading(false);
      setError(null);
      return;
    }
    const seq = ++searchSeqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetcher(user.id);
      if (seq !== searchSeqRef.current) return; // stale 검색 응답 무시
      setResult({ orders: data.orders, isPartial: data.isPartial, mode, query: q });
      if (data.isPartial) {
        logError('대시보드 검색 결과 잘림 감지 (max_rows)', { mode, q });
      }
    } catch (err) {
      if (seq !== searchSeqRef.current) return;
      logError('대시보드 주문 검색 실패:', err);
      setError(err as Error);
    } finally {
      if (seq === searchSeqRef.current) setIsLoading(false);
    }
  }, [user]);

  // 고객 suggestion 클릭 경로 — 기존 handleSearch 시맨틱(setSearchQuery+close+fetch) 유지 — hasSearched는 result 게이트로 대체되어 제거(최종 리뷰 Minor #1).
  // 닉네임도 부분일치 (Enter 경로와 동일 RPC, 윈도우는 서버 KST -90~+30일). Order[] → SearchedOrder 승격.
  const searchByNickname = useCallback((nickname: string) => {
    setSearchQuery(nickname);
    setShowSuggestions(false);
    runSearch('nickname', nickname, async (userId) => {
      const data = await orderService.findOrdersByNicknameWithItems(userId, nickname);
      const orders: SearchedOrder[] = data.orders.map(o => ({
        ...o,
        matchTypes: ['nickname'] as OrderSearchMatchType[],
        matchedItemIds: [] as number[],
      }));
      return { orders, isPartial: data.isPartial };
    });
  }, [runSearch]);

  const searchByProduct = useCallback((name: string) => {
    setSearchQuery(name);
    setShowSuggestions(false);
    runSearch('product', name, (userId) => orderService.findOrdersByItemNameWithItems(userId, name));
  }, [runSearch]);

  const searchUnified = useCallback((q: string) => {
    setSearchQuery(q);
    setShowSuggestions(false);
    runSearch('all', q, (userId) => orderService.findOrdersUnifiedWithItems(userId, q));
  }, [runSearch]);

  return {
    searchQuery,
    suggestions,
    isSuggesting,
    showSuggestions,
    closeSuggestions,
    openSuggestions,
    onQueryChange,
    searchByNickname,
    searchByProduct,
    searchUnified,
    result,
    isLoading,
    error,
  };
}
